<script setup lang="ts">
import { VDataTableServer } from 'vuetify/labs/VDataTable'
import { useDrawingStore } from '@/store/drawing'
import { $getProjectLocationModifiedFiles } from '@/api/locations'
import { $copySignContentToSigns, $getSigns, $signsGlobalOptions } from '@/api/signs'
import MultipleSignTypeSelect from '@/components/helpers/selects/sign-type/MultipleSignTypeSelect.vue'

// types
import type { TableOptions, SortByOption } from '@/models/table-options'
import type { SideOption } from '@/models/wayfinding/copy-sign-content'
import type { Sign } from '@/models/sign'

interface IProps {
  modelValue?: boolean,
  sign: any,
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: false,
  sign: null,
})
interface Emit {
  (e: 'update:modelValue', value: boolean): void
  (e: 'onClose'): void
  (e: 'onSubmit'): void
}

const emit = defineEmits<Emit>()

const route = useRoute()
const drawingStore = useDrawingStore()

const selectedCampaign = ref<any>(null)
const selectedLocation = ref<any>(null)
const signTypeFilter = ref<any[]>([])
const signTypeOptions = ref<any[]>([])
const signNameFilter = ref<string>('')
const globalOptionsLoading = ref<boolean>(false)

const signs = ref<any[]>([])
const signsLoading = ref<boolean>(false)
const signsCount = ref<number>(0)
const selectedSignIds = ref<number[]>([])
const selectedSignIdsSideMappings = ref<Record<string, Record<string, number | null>>>({})
const sideMappings = ref<Record<string, Record<string, number | null>>>({})
const signTableOptions = ref<TableOptions>({
  itemsPerPage: 10,
  page: 1,
  sortBy: []
})


const signTableHeaders = [
  { key: 'sign_code', title: 'Code', sortable: false, align: '', selectable: true },
  { key: 'side_a', title: 'Side A', sortable: false, align: 'center' },
  { key: 'side_b', title: 'Side B', sortable: false, align: 'center' },
  { key: 'side_c', title: 'Side C', sortable: false, align: 'center' },
  { key: 'side_d', title: 'Side D', sortable: false, align: 'center' },
]

const itemsPerPageOptions = [
  { value: 15, title: '15' },
  { value: 20, title: '20' },
  { value: 50, title: '50' },
  { value: 100, title: '100' },
]

const copyLoading = ref<boolean>(false)

const isMounted = ref<boolean>(false)

// Location options
const locationsLoading = ref<boolean>(false)
const locationOptions = ref<any[]>([])

// Side options for dropdowns
const sideOptions = computed<SideOption[]>(() => {
  const baseOptions = [
    { title: 'None', value: null },
    { title: 'A', value: 0 },
    { title: 'B', value: 1 },
    { title: 'C', value: 2 },
    { title: 'D', value: 3 }
  ]

  if (!props?.sign || ![0, 1, 2, 3].includes(props?.sign?.type_info?.sides)) {
    return baseOptions
  }

  return baseOptions.filter(i => i.value === null || i.value <= props?.sign?.type_info?.sides)
})

const getLocations = async () => {
  locationsLoading.value = true
  try {
    const res = await $getProjectLocationModifiedFiles({
      campaign: selectedCampaign.value ? selectedCampaign.value.id : undefined, // filter by selected Campaign
      image_exists: true
    })

    locationOptions.value = res.data.results
  } catch (e) {
    console.log(e)
  } finally {
    locationsLoading.value = false
  }
}

const getGlobalSignsOptions = async () => {
  globalOptionsLoading.value = true
  try {
    const res = await $signsGlobalOptions({
      filters: JSON.stringify({
        campaign: selectedCampaign.value?.id,
        location: selectedLocation.value?.location,
      })
    })
    signTypeOptions.value = res.data.sign_type_options
  } catch (e) {
    console.log(e)
  } finally {
    globalOptionsLoading.value = false
  }
}

const onChangeCampaign = () => {
  if (selectedCampaign.value) {
    getLocations()
  }

  if (selectedLocation.value) {
    selectedLocation.value = null
  }

  // Get Signs on change Campaign filter
  getSigns()
  getGlobalSignsOptions()
}

const onChangeLocation = () => {
  // Get Signs on change Location filter
  getSigns()
  getGlobalSignsOptions()
}

const onChangeSignType = () => {
  // Get Signs on change Sign Type filter
  getSigns()
}

const onChangeSignName = () => {
  // Get Signs on change Sign Name filter
  getSigns()
}

const copySignContentToSigns = async () => {
  if (!props.sign?.id) return

  copyLoading.value = true

  const payload = {
    copy_from_sign_id: props.sign?.id,
    copy_to_sign_ids: selectedSignIds.value,
    side_mappings: selectedSignIdsSideMappings.value,
  }
  try {
    await $copySignContentToSigns(payload)

    // clear
    selectedSignIds.value = []
    selectedSignIdsSideMappings.value = {}
    sideMappings.value = {}

    emit('onSubmit')
    emit('update:modelValue', false)
  } catch (e) {
    console.log(e)
  } finally {
    copyLoading.value = false
  }
}

const closeDialog = () => {
  emit('update:modelValue', !props.modelValue)
}

const onClose = async () => {
  selectedCampaign.value = null
  selectedLocation.value = null
  signTypeFilter.value = []
  signNameFilter.value = ''
  emit('onClose')
}

const getSigns = async () => {
  signsLoading.value = true

  try {
    const res = await $getSigns({
      is_copy_sign_content_list: true,  // id, sign_code and type_sides_number fields only
      page: signTableOptions.value.page,
      per_page: signTableOptions.value.itemsPerPage,
      campaign: selectedCampaign.value?.id,
      location: selectedLocation.value?.location,
      is_removed: false,
      type__trigram__in: signTypeFilter.value.length ? signTypeFilter.value.map(i => i.trigram).join(',') : undefined,
      sign_code__icontains: signNameFilter.value,
    })

    signs.value = res.data.results
    signs.value.forEach(sign => { sign.side_a = 0; sign.side_b = 0; sign.side_c = 0; sign.side_d = 0 })
    signsCount.value = res.data.count

    signsLoading.value = false

    // Initialize side mappings for each sign
    const sideOptionsValues = sideOptions.value.map(i => i.value)
    sideMappings.value = signs.value.reduce((acc, sign) => {
      const existingSelection = selectedSignIdsSideMappings.value[sign.id]

      if (existingSelection) {
        acc[sign.id] = {...existingSelection}
      } else {
        const sides = {} as Record<string, number | null>
        for (let i = 0; i <= sign?.type_sides_number || 0; i++) {
          // If Sign Reprint - set Side Value only for Reprint Side
          if (sign?.is_reprint) {
            sides[i] = sign?.reprint_side === i ? (sideOptionsValues.includes(i) ? i : null) : null
          }
          else {
            sides[i] = sideOptionsValues.includes(i) ? i : null
          }
        }
        acc[sign.id] = sides
      }

      return acc
    }, {} as Record<string, Record<string, number | null>>)
  } catch (e: any) {
    // If page doesn't exist goes to 1 page
    if (e?.response?.status === 404) {
      signTableOptions.value.page = 1
      await getSigns()
    } else {
      console.log(e)
    }
  }
}


const onUpdateTableOptions = async (
  { page, itemsPerPage, sortBy }: { page: number; itemsPerPage: number; sortBy: SortByOption[] }
) => {
  signTableOptions.value.page = page
  signTableOptions.value.itemsPerPage = itemsPerPage
  signTableOptions.value.sortBy = sortBy
  if (isMounted.value) {
    await getSigns()
  }
}

const onTableSelect = () => {
  const currentPageSignIds = Object.keys(sideMappings.value)
  const selectedSignIdsStrings = selectedSignIds.value.map(String)

  // Create sets for O(1) lookups
  const currentPageIdsSet = new Set(currentPageSignIds)
  const selectedSignIdsSet = new Set(selectedSignIdsStrings)

  // Initialize empty objects with proper typing
  const selectedSideMappingsNotInPage: Record<string, Record<string, number | null>> = {}
  const currentPageSelectedSideMappings: Record<string, Record<string, number | null>> = {}

  // Single pass through selectedSignIdsSideMappings
  for (const [signId, sideMapping] of Object.entries(selectedSignIdsSideMappings.value)) {
    if (!currentPageIdsSet.has(signId)) {
      selectedSideMappingsNotInPage[signId] = sideMapping
    }
  }

  // Single pass through sideMappings
  for (const [signId, sideMapping] of Object.entries(sideMappings.value)) {
    if (selectedSignIdsSet.has(signId)) {
      currentPageSelectedSideMappings[signId] = sideMapping
    }
  }

  // Merge objects (spread operator is fastest for this case)
  selectedSignIdsSideMappings.value = {
    ...selectedSideMappingsNotInPage,
    ...currentPageSelectedSideMappings
  }
}

onMounted(async () => {
  isMounted.value = true
})

watch(() => props.modelValue, () => {
  // On show dialog
  if (props.modelValue) {
    selectedCampaign.value = { id: props.sign?.campaign, name: props.sign?.campaign_name}
    selectedLocation.value = { location: props.sign?.location, name: props.sign?.location_name}

    getLocations()
    getGlobalSignsOptions()
  }
})

interface TableItem {
  value: number | string
  raw?: Sign
}

const shouldShowSide = (item: TableItem, sideNo: number): boolean => {
  if (item.raw?.is_reprint) {
    return item.raw?.reprint_side === sideNo
  }

  const signTypeSidesNumber = item?.raw?.type_sides_number || 0

  // Type-safe access to sideMappings
  const mappingKey = item.value.toString()
  const hasMapping = sideMappings.value[mappingKey] !== undefined

  return hasMapping && signTypeSidesNumber >= sideNo
}

const showSideA = (item: any) => shouldShowSide(item, 0)
const showSideB = (item: any) => shouldShowSide(item, 1)
const showSideC = (item: any) => shouldShowSide(item, 2)
const showSideD = (item: any) => shouldShowSide(item, 3)
</script>

<template>
  <VDialog
    :model-value="modelValue"
    :persistent="!!copyLoading"
    max-width="800"
    class="default-dialog"
    @after-leave="onClose"
    @update:modelValue="emit('update:modelValue', $event)"
  >
    <VBtn
      variant="elevated"
      density="compact"
      icon="tabler-x"
      :disabled="!!copyLoading"
      class="v-dialog-close-btn"
      @click="closeDialog"
    />

    <VCard>
      <template #title>
        Copy Content to Signs
      </template>
      <VCardText>
        <VRow>
          <VCol cols="12">
            <CampaignSelect
              v-model="selectedCampaign"
              :disabled="copyLoading"
              :type="1"
              @update:modelValue="onChangeCampaign"
            />
          </VCol>
          <VCol
            cols="12"
            class="position-relative"
          >
            <LocationSelect
              v-model="selectedLocation"
              :select-options="[...locationOptions]"
              :disabled="!selectedCampaign || copyLoading"
              @update:modelValue="onChangeLocation"
            />
            <VOverlay
              v-model="locationsLoading"
              persistent
              absolute
              contained
              class="align-center justify-center"
            >
              <VProgressCircular
                indeterminate
                color="primary"
              />
            </VOverlay>
          </VCol>
          <VCol cols="12" md="6">
            <MultipleSignTypeSelect
              v-model="signTypeFilter"
              :select-options="signTypeOptions"
              :loading="globalOptionsLoading"
              @update:modelValue="onChangeSignType"
            />
          </VCol>
          <VCol cols="12" md="6">
            <VTextField
              v-model="signNameFilter"
              label="Search..."
              density="compact"
              :disabled="copyLoading"
              @update:modelValue="onChangeSignName"
              clearable
            />
          </VCol>
        </VRow>
        <VRow>
          <VCol class="pt-0">
            <VDataTableServer
              v-model="selectedSignIds"
              v-model:page="signTableOptions.page"
              :items="signs"
              :items-length="signsCount"
              :headers="signTableHeaders"
              :loading="signsLoading"
              no-data-text="Signs not found"
              density="compact"
              id="copy-sign-content-signs-table"
              key="id"
              show-select
              hover
              show-current-page
              :items-per-page-options="itemsPerPageOptions"
              :items-per-page="signTableOptions.itemsPerPage"
              @update:options="onUpdateTableOptions"
              @update:modelValue="newValue => onTableSelect(newValue)"
            >
              <template #top>
                <VChip>
                  Selected {{ selectedSignIds.length }} sign(s)
                </VChip>
              </template>
              <template #item.side_a="{ item }">
                <VSelect
                  v-if="showSideA(item)"
                  :items="sideOptions"
                  density="compact"
                  variant="plain"
                  hide-details
                  :model-value="sideMappings?.[item?.value]?.[0] ?? null"
                  @update:modelValue="val => {
                    if (sideMappings?.[item?.value]?.[0] !== undefined) {
                      sideMappings[item.value][0] = val
                    }
                  }"
                />
              </template>
              <template #item.side_b="{ item }">
                <VSelect
                  v-if="showSideB(item)"
                  :items="sideOptions"
                  density="compact"
                  variant="plain"
                  hide-details
                  :model-value="sideMappings?.[item?.value]?.[1] ?? null"
                  @update:modelValue="val => {
                    if (sideMappings?.[item?.value]?.[1] !== undefined) {
                      sideMappings[item.value][1] = val
                    }
                  }"
                />
              </template>
              <template #item.side_c="{ item }">
                <VSelect
                  v-if="showSideC(item)"
                  :items="sideOptions"
                  density="compact"
                  variant="plain"
                  hide-details
                  :model-value="sideMappings?.[item?.value]?.[2] ?? null"
                  @update:modelValue="val => {
                    if (sideMappings?.[item?.value]?.[2] !== undefined) {
                      sideMappings[item.value][2] = val
                    }
                  }"
                />
              </template>
              <template #item.side_d="{ item }">
                <VSelect
                  v-if="showSideD(item)"
                  :items="sideOptions"
                  density="compact"
                  variant="plain"
                  hide-details
                  :model-value="sideMappings?.[item?.value]?.[3] ?? null"
                  @update:modelValue="val => {
                    if (sideMappings?.[item?.value]?.[3] !== undefined) {
                      sideMappings[item.value][3] = val
                    }
                  }"
                />
              </template>
            </VDataTableServer>
          </VCol>
        </VRow>
        <VRow>
          <VCol class="text-right">
            <VBtn
              class="py-2 px-3"
              size=""
              color="primary"
              :disabled="!selectedSignIds?.length || !!copyLoading"
              @click="copySignContentToSigns"
            >
              Copy Content to Selected Signs
              <VProgressCircular
                v-if="copyLoading"
                indeterminate
                size="16"
                width="2"
                class="ml-1"
                color="light"
              />
            </VBtn>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style lang="scss">
#copy-sign-content-signs-table {
  table {
    thead > tr > th {
      font-size: 10px;
      border-right: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
      padding: 0 10px;
    }
    tbody {
      font-size: 10px;
      tr > td {
        padding: 0 10px;
      }
    }
  }
  .v-data-table-footer {
    justify-content: center !important;
  }
  .v-select {
    min-width: 60px;
  }
}
</style>
